import React, { useMemo, useState, } from "react";
import { useTranslation } from 'react-i18next';
import { UserBadge, UserBadgeStack } from "@/components/shared";
import { ActivityType, MasteryStatusPacing, MasteryTimeBooked } from "@/enums";
import { LessonDetails, Skill, StudentAssignment } from "@/models";
import { countResourcesAssigned, getSkillTimeBalance } from "@/utilities";
import CaretSvg from "../icons/thin-caret.svg";
import AssignmentModal from "@/features/assignment_modal/components/AssignmentModal";
import editModalIcon from "../icons/edit-modal.svg";
import cx from "classnames";
import { isBeforeCurrentWeek } from "@/utilities/isBeforeCurrentWeek";
import { AmiraResources } from "@/models/AmiraResources.interface";
import MicrolessonSvg from "../icons/microlesson.svg";
import TutorSvg from "../icons/tutor.svg";
import TeacherResourceSvg from "../icons/teacher-resource.svg";
import InfoIcon from "../icons/infoicon.svg";
import TimeToGrowthIcon from "../icons/time-to-growth-icon.svg";
import StopWatchIcon from "@/images/icons/stopwatch.svg"
import "./StudentMasteryGroup.scss";
import ClockIcon from "../icons/ClockIcon";
import { MasteryBadge } from './MasteryBadge'
import { Placeholder } from "@/components/Global";
import { transformId } from "@/utilities/transformId";
import TimeToGrowthDurationEstimate from "@/components/shared/TimeToGrowthDurationEstimate";
const TIME_ASSIGNED_THRESHOLD_MINUTES = 2;

export interface StudentMasteryGroupProps {
  lesson: LessonDetails;
  skill: Skill;
  groupName: string;
  students: StudentAssignment[];
  resources: number;
  microlessons: number;
  tutoring: number;
  isMicrolessonsAverage: boolean;
  isResourceAverage: boolean;
  isTutoringAverage: boolean;
  timeAssigned: number;
  skillResources: AmiraResources[];
  collapseStates: { [key: string]: boolean };
  toggleCollapse: (val: string) => void;
  standards: any;
  source: string;
  areResourcesLoading: boolean;
}

export const StudentMasteryGroup: React.FC<StudentMasteryGroupProps> = ({
  lesson,
  skill,
  groupName,
  students,
  resources,
  microlessons,
  tutoring,
  isMicrolessonsAverage,
  isResourceAverage,
  isTutoringAverage,
  skillResources,
  collapseStates,
  toggleCollapse,
  source,
  standards,
  timeAssigned: averageTimeAssigned,
  areResourcesLoading,
}) => {
  const { t } = useTranslation();
  const [isAssignmentModalOpen, setIsAssignmentModalOpen] = useState(false);
  const [activeStudentId, setActiveStudentId] = useState<string | null>(null);
  const handleAddStudentAssignments = (studentId?: string) => {
    setActiveStudentId(studentId ?? null);
    setIsAssignmentModalOpen(true);
  };
  const { timeToGrowth: groupTimeToGrowth, skillBookingStatus: groupSkillBookingStatus } = useMemo(() => getSkillTimeBalance(
    skill.timeToMaster ?? 0,
    averageTimeAssigned,
    groupName as MasteryStatusPacing,
    TIME_ASSIGNED_THRESHOLD_MINUTES
  ), [
    skill.timeToMaster,
    averageTimeAssigned,
  ]);
  // console.log(`-------======> timeToGrowth: ${timeToGrowth}, timeToMaster: ${skill.timeToMaster}, averageTimeAssigned: ${averageTimeAssigned}, groupName: ${groupName}`)
  const timeAssignedClassName = cx({
    "time-assigned-badge": true,
    "rounded-2": true,
    "text-bg-amira-purple-light": groupSkillBookingStatus === MasteryTimeBooked.ON_TRACK || groupSkillBookingStatus === MasteryTimeBooked.NO_DATA,
    "text-bg-danger": groupSkillBookingStatus === MasteryTimeBooked.UNDER_BOOKED || groupSkillBookingStatus === MasteryTimeBooked.OVER_BOOKED,
  });

  const {
    caretClassName,
    caretAltText,
    identifier,
  } = useMemo(() => {
    const identifier = `${skill.skillId?.replaceAll(".", "-")}-${groupName}`;
    return {
      identifier,
      caretClassName: collapseStates[identifier] ? "caret-expanded" : "caret-collapsed",
      caretAltText: collapseStates[identifier] ? "Collapse Mastery Group" : "Expand Mastery Group"
    }
  }, [collapseStates, skill.skillId, groupName])

  const isAddResourcesDisabled = isBeforeCurrentWeek(lesson.dateFrom.split(" ")[0]);

  // Calculate the combined and unique manifest after initial filtering
  const allFilteredManifestItems = students.flatMap(s =>
    s.assignment
      ? s.assignment.manifest.filter(m =>
          m.skills?.some(sk => sk.skillId === skill.skillId)
        ) ?? []
      : []
  );

  // Deduplicate the manifest items.
  // IMPORTANT: manifest can be huge due to the number of students in a group having different manifests.
  const uniqueGlobalManifest = Array.from(
    new Map(allFilteredManifestItems.map(item => {
      const key = transformId((item as any).storyId);
      return [key, item];
    })).values()
  );

  return (
    <table className="student-table-header-container-lesson-detail">
      <tbody
        className={`d-flex flex-column ${caretClassName === "caret-expanded" ? "gap-3" : "gap-0"}`}>
        <tr className={`student-table-header-lesson-detail group-row`}>
          <td>
            <button
              className="student-collapse-button"
              onClick={() => toggleCollapse(identifier)}>
              <img
                className={caretClassName}
                src={CaretSvg}
                alt={caretAltText}
              />
            </button>
          </td>
          <td className="group-name d-flex">
            <div>
              <MasteryBadge status={groupName} />
            </div>
          </td>
          <td className="group-stack">
            <UserBadgeStack
              students={students.map(
                ({ student: { first_name, last_name } }) =>
                  `${first_name} ${last_name}`
              )}
            />
          </td>
          <td className="count-resource d-flex flex-column align-items-center gap-2">
            <div className="d-flex align-items-center gap-1">
              <span className="count-resource-text">{`${isResourceAverage ? "*" : ""}${resources}`}</span>
              <img
                src={TeacherResourceSvg}
                alt="teacher resource"
                className="resource-icon"
              />
            </div>
            <span className="resource-text">teacher resources</span>
          </td>
          <td className="count-microlesson d-flex flex-column align-items-center gap-2">
            <div className="d-flex align-items-center gap-1">
              <span className="count-microlesson-text">{`${isMicrolessonsAverage ? "*" : ""}${microlessons}`}</span>
              <img
                src={MicrolessonSvg}
                alt="microlesson"
                className="resource-icon"
              />
            </div>
            <span className="resource-text">microlessons</span>
          </td>
          <td className="count-tutor d-flex flex-column align-items-center gap-2">
            <div className="d-flex align-items-center gap-1">
              <span className="count-tutor-text">{`${isTutoringAverage ? "*" : ""}${tutoring}`}</span>
              <img src={TutorSvg} alt="tutor" className="resource-icon" />
            </div>
            <span className="resource-text">tutor</span>
          </td>
          <td className="time-to-growth-lesson-detail">
            <div className="d-flex flex-column align-items-center justify-content-center gap-2">
              <span className="time-to-growth-text">
                Time to Growth{" "}
                <img src={InfoIcon} alt="info" className="info-icon" />
              </span>
              <span className="time-to-growth-badge">
                <img
                  src={TimeToGrowthIcon}
                  alt="time to growth"
                  className="time-to-growth-icon"
                />
                {`${groupTimeToGrowth} mins`}
              </span>
            </div>
          </td>
          <td className="time-assigned-lesson-detail">
            <div className="d-flex flex-column align-items-center justify-content-center gap-2">
              <span className="time-assigned-text">Time Assigned</span>
              {areResourcesLoading ? (
                <Placeholder colWidth={6} heightInRem={1.5} />
              ) : (
                <TimeToGrowthDurationEstimate 
                  duration={averageTimeAssigned} 
                  warning={groupSkillBookingStatus === MasteryTimeBooked.UNDER_BOOKED || groupSkillBookingStatus === MasteryTimeBooked.OVER_BOOKED}
                  timeAssignedIcon={StopWatchIcon}
                />
              )}
            </div>
          </td>
          <td className="open-assignment-modal">
            {areResourcesLoading ? (
              <Placeholder colWidth={12} heightInRem={1.75} />
            ) : (
              <button
                className="open-assignment-modal-button"
                data-testid="open-assignment-modal-button"
                onClick={() => handleAddStudentAssignments()}
                disabled={isAddResourcesDisabled}>
                <img
                  className="open-assignment-modal-icon"
                  src={editModalIcon}
                  alt="open assignment modal"
                />
              </button>
            )}
          </td>
          {activeStudentId === null && (
            <AssignmentModal
              isOpen={isAssignmentModalOpen}
              onCloseHandler={() => setIsAssignmentModalOpen(false)}
              searchHeader="Add High-Quality Instructional Materials"
              resultsHeader="Search Results"
              isTutoring={false}
              groupName={groupName}
              students={students.map(s => ({
                ...s,
                assignment: s.assignment
                  ? {
                      ...s.assignment,
                      manifest: uniqueGlobalManifest,
                    }
                  : undefined,
              }))}
              lesson={lesson}
              skillName={skill.displayName || skill.skillName || skill.skillId!}
              skillId={skill.skillId!}
              skill={skill}
              skillMastery={skill.timeToMaster ?? 0}
              skillResources={skillResources}
              standards={standards}
              source={source}
            />
          )}
        </tr>
        <tr>
          <td className="p-0 d-flex w-100">
            <table className="student-table-row-container w-100">
              <tbody className="d-flex flex-column align-items-center gap-3 w-100">
                {collapseStates[
                  `${skill.skillId?.replaceAll(".", "-")}-${groupName}`
                ] &&
                  students.map(({ student, assignment }) => {
                    const { id: studentId, first_name, last_name } = student;
                    let [resources, microlessons, tutoring] = [0, 0, 0];
                    if (assignment && assignment.manifest.length) {
                      const skillResources = lesson.skillResources.find(r => r.skillId === skill.skillId)?.resources || [];
                      const multiStoryResources = skillResources.filter(r => r.storyIds?.length && r.storyIds.length > 0);
                      const processedMultiStoryResources = new Set<string>();
                      assignment.manifest.forEach(story => {
                        if (
                          !story.skills?.length ||
                          story.skills[0].skillId !== skill.skillId
                        ) {
                          return;
                        }
                        if (
                          story.activityType === ActivityType.MICRO_LESSON ||
                          story.activityType ===
                            ActivityType.ISTATION_LEGACY_ACTIVITY
                        ) {

                          microlessons += 1;
                        } else if (
                          story.activityType === ActivityType.AMIRA_STORY ||
                          story.activityType === ActivityType.TUTOR
                        ) {
                          const matchedMultiStoryResource = multiStoryResources.find(r => r.storyIds?.includes(story.storyId || ''));
                          if (matchedMultiStoryResource && processedMultiStoryResources.has(matchedMultiStoryResource.resourceUniqueId || '')){
                            return;
                          }
                          if (matchedMultiStoryResource) {
                            processedMultiStoryResources.add(matchedMultiStoryResource.resourceUniqueId || '');
                          }
                          tutoring += 1;
                        }
                      });
                    }
                    resources += countResourcesAssigned(
                      lesson.resources,
                      skill,
                      assignment
                    );
                    const individualTimeAssigned =
                      lesson.timeAssigned?.[skill.skillId ?? ""]?.[groupName ?? '']?.[studentId] ??
                      0;
                    const {
                      timeToGrowth: individualTimeToGrowth,
                      skillBookingStatus: individualSkillBookingStatus,
                    } = getSkillTimeBalance(
                      skill.timeToMaster ?? 0,
                      individualTimeAssigned,
                      groupName as MasteryStatusPacing,
                      TIME_ASSIGNED_THRESHOLD_MINUTES
                    );
                    const timeAssignedClassName = cx({
                      "time-assigned-badge": true,
                      "rounded-2": true,
                      "text-bg-amira-purple-light":
                        individualSkillBookingStatus ===
                          MasteryTimeBooked.ON_TRACK ||
                        individualSkillBookingStatus ===
                          MasteryTimeBooked.NO_DATA,
                      "text-bg-danger":
                        individualSkillBookingStatus ===
                          MasteryTimeBooked.UNDER_BOOKED ||
                        individualSkillBookingStatus ===
                          MasteryTimeBooked.OVER_BOOKED,
                    });

                    return (
                      <tr
                        className="student-mastery-group-row w-100"
                        key={`mastery-student-group-${skill.skillId}-${studentId}`}>
                        <td className="group-name d-flex align-items-center justify-content-start">
                          <div className="d-flex align-items-center gap-2">
                            <UserBadge name={`${first_name} ${last_name}`} />
                            <span className="individual-student-name">{`${first_name} ${last_name}`}</span>
                          </div>
                        </td>
                        <td className="count-resource">
                          <div className="d-flex align-items-center gap-2">
                            {resources}
                            <img
                              src={TeacherResourceSvg}
                              alt="teacher resource"
                              className="resource-icon"
                            />
                          </div>
                        </td>
                        <td className="count-microlesson">
                          <div className="d-flex align-items-center gap-2">
                            {microlessons}
                            <img
                              src={MicrolessonSvg}
                              alt="microlesson"
                              className="resource-icon"
                            />
                          </div>
                        </td>
                        <td className="count-tutor">
                          <div className="d-flex align-items-center gap-2">
                            {tutoring}
                            <img
                              src={TutorSvg}
                              alt="tutor"
                              className="resource-icon"
                            />
                          </div>
                        </td>
                        <td className="time-to-growth-lesson-detail">
                          <div className="d-flex flex-column align-items-center justify-content-center gap-2">
                            <span className="time-to-growth-text">
                              Time to Growth{" "}
                              <img
                                src={InfoIcon}
                                alt="info"
                                className="info-icon"
                              />
                            </span>
                            <span className="time-to-growth-badge">
                              <img
                                src={TimeToGrowthIcon}
                                alt="time to growth"
                                className="time-to-growth-icon"
                              />
                              {`${individualTimeToGrowth} mins`}
                            </span>
                          </div>
                        </td>
                        <td className="time-assigned-lesson-detail">
                          <div className="d-flex flex-column align-items-center justify-content-center gap-2">
                            <span className="time-assigned-text">
                              Time Assigned
                            </span>
                            {areResourcesLoading ? (
                              <Placeholder colWidth={6} heightInRem={1.5} />
                            ) : (
                               <TimeToGrowthDurationEstimate 
                                duration={individualTimeAssigned} 
                                warning={individualSkillBookingStatus === MasteryTimeBooked.UNDER_BOOKED || individualSkillBookingStatus === MasteryTimeBooked.OVER_BOOKED}
                                timeAssignedIcon={StopWatchIcon}
                              />
                            )}
                          </div>
                        </td>
                        <td className="open-assignment-modal">
                          {areResourcesLoading ? (
                            <Placeholder colWidth={12} heightInRem={1.75} />
                          ) : (
                            <button
                              className="open-assignment-modal-button"
                              data-testid="open-assignment-modal-button"
                              onClick={() =>
                                handleAddStudentAssignments(studentId)
                              }
                              disabled={isAddResourcesDisabled}>
                              <img
                                className="open-assignment-modal-icon"
                                src={editModalIcon}
                                alt="open assignment modal"
                              />
                            </button>
                          )}
                        </td>
                        {activeStudentId === studentId && (
                          <AssignmentModal
                            isOpen={isAssignmentModalOpen}
                            onCloseHandler={() =>
                              setIsAssignmentModalOpen(false)
                            }
                            searchHeader="Add High-Quality Instructional Materials"
                            resultsHeader="Search Results"
                            isTutoring={false}
                            groupName={groupName}
                            students={[
                              {
                                student,
                                assignment: assignment,
                              },
                            ]}
                            lesson={lesson}
                            skillName={
                              skill.displayName ||
                              skill.skillName ||
                              skill.skillId!
                            }
                            skillId={skill.skillId!}
                            skill={skill}
                            skillResources={skillResources}
                            standards={standards}
                            skillMastery={skill.timeToMaster ?? 0}
                            source={source}
                          />
                        )}
                      </tr>
                    );
                  })}
              </tbody>
            </table>
          </td>
        </tr>
      </tbody>
    </table>
  );
};

export default StudentMasteryGroup;
